/**
 * TrainerInvitation — public page that finalizes an extranet invitation.
 * Reads ?token=, validates it, asks for password (+ name), POSTs accept,
 * stores the returned session_token, redirects to /trainer.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import Logo from '../../components/Logo';

export default function TrainerInvitation() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, error: null, info: null });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, error: 'Lien invalide', info: null }); return; }
    api.get(`/public/trainer-invitations/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setName(data.trainer_name || '');
        setState({ loading: false, valid: true, error: null, info: data });
      })
      .catch((e) => setState({ loading: false, valid: false, error: e?.response?.data?.detail || 'Lien invalide', info: null }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return alert('Mot de passe : minimum 8 caractères');
    if (password !== password2) return alert('Les mots de passe ne correspondent pas');
    setSubmitting(true);
    try {
      const { data } = await api.post('/public/trainer-invitations/accept', {
        token, password, name: name.trim() || undefined,
      });
      sessionStorage.setItem('ifaap_session_token', data.session_token);
      navigate(data.redirect || '/trainer', { replace: true });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Échec de l\'activation');
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>;
  }
  if (!state.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#F6F7FB]">
        <div className="soft-card p-6 max-w-md text-center" data-testid="trainer-invitation-error">
          <div className="w-12 h-12 rounded-full bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center mx-auto mb-3"><AlertCircle className="w-5 h-5" /></div>
          <h1 className="text-[16px] font-bold text-[#1A1B2E] mb-1">Lien invalide</h1>
          <p className="text-[13px] text-[#6B6C85]">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[#F6F7FB]">
      <div className="soft-card p-6 max-w-md w-full" data-testid="trainer-invitation-form">
        <div className="flex items-center justify-between mb-4">
          <Logo size={44} variant="full" />
          <span className="text-[11px] text-[#8E8FA5]">Espace formateur</span>
        </div>
        <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Activez votre accès</h1>
        <p className="text-[13px] text-[#6B6C85] mb-4">
          <strong>{state.info?.org_name}</strong> vous invite. Définissez votre mot de passe pour accéder à votre extranet formateur.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Email</label>
            <input value={state.info?.email || ''} disabled className="w-full h-10 px-3 rounded-xl bg-[#F3F4F8] border border-[#EFF0F6] text-[13px] text-[#6B6C85]" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Nom complet</label>
            <input data-testid="trainer-inv-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Mot de passe (8+ car.)</label>
            <input data-testid="trainer-inv-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none" />
          </div>
          <div>
            <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1">Confirmation</label>
            <input data-testid="trainer-inv-password2" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} required className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] outline-none" />
          </div>
          <button type="submit" disabled={submitting} data-testid="trainer-inv-submit" className="w-full h-11 rounded-xl brand-gradient text-white text-[13.5px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Activer mon espace
          </button>
        </form>
      </div>
    </div>
  );
}
